import { useEffect, useMemo, useRef, useState } from "react";
import { useBoolean } from "@fluentui/react-hooks";
import buttonCopyIcon from "../../assets/button_copy.svg";
import { HiChevronUp, HiChevronDown } from "react-icons/hi2";
import { Stack, Text, DefaultButton } from "@fluentui/react";
import { useTexts } from "../../context/TextProvider";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow } from "react-syntax-highlighter/dist/esm/styles/prism";

import styles from "./Answer.module.css";

import { AskResponse, Citation, CitationWithReference } from "../../api";
import { parseAnswer } from "./AnswerParser";
import FeedbackButton from "../Feedback/Feedback";
import uuid from "react-uuid";

import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import supersub from "remark-supersub";
import "katex/dist/katex.min.css";

interface Props {
    answer: AskResponse;
    onCitationClicked: (citedDocument: CitationWithReference) => void;
    traceId?: string;
    showActions?: boolean;
}

export const Answer = ({ answer, onCitationClicked, traceId, showActions = true }: Props) => {
    const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] =
        useBoolean(false);
    const filenameTruncationLimit = 50;
    const { texts } = useTexts();

    const parsedAnswer = useMemo(() => parseAnswer(answer), [answer]);
    const messageId = useRef(uuid()).current;
    const [chevronIsExpanded, setChevronIsExpanded] =
        useState(isRefAccordionOpen);

    const handleChevronClick = () => {
        setChevronIsExpanded(!chevronIsExpanded);
        toggleIsRefAccordionOpen();
    };

    useEffect(() => {
        setChevronIsExpanded(isRefAccordionOpen);
    }, [isRefAccordionOpen]);

    const createCitationFilename = (
        citation: Citation,
        index: number
    ) => {
        let citationFilename = "";
        let citationFilenameTruncated = "";

        if (citation.filename) {
            const strippedFilename = citation.filename.replace(/\.[^/.]+$/, "");
            citationFilename = strippedFilename;
            if (strippedFilename.length > filenameTruncationLimit) {
                citationFilenameTruncated = `${strippedFilename.substring(
                    0,
                    filenameTruncationLimit - 3
                )}...`;
            } else {
                citationFilenameTruncated = `${strippedFilename}`;
            }
        } else {
            citationFilename = `Zitat ${index}`;
        }

        return {
            title: `${index} ${citationFilename}`,
            titleTruncated: citationFilenameTruncated,
        };
    };

    return (
        <Stack className={styles.answerContainer} tabIndex={0}>
            <div className={styles.answerTextContainer}>
                <ReactMarkdown
                    linkTarget="_blank"
                    remarkPlugins={[remarkGfm, supersub, remarkMath]}
                    // @ts-expect-error
                    rehypePlugins={[rehypeKatex]}
                    components={{
                        code(props) {
                            const { children, className, node, ...rest } = props;
                            const match = /language-(\w+)/.exec(className ?? "");
                            return match ? (
                                <SyntaxHighlighter
                                    {...rest}
                                    PreTag="div"
                                    children={String(children).replace(/\n$/, "")}
                                    language={match[1]}
                                    style={tomorrow}
                                />
                            ) : (
                                <code {...rest} className={className}>
                                    {children}
                                </code>
                            );
                        },
                    }}
                    children={parsedAnswer.markdownFormatText}
                    className={styles.answerText}
                />
            </div>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={styles.answerCitations}>
                {!!parsedAnswer.citations.length && (
                    <Stack.Item
                        onKeyDown={(e) =>
                            e.key === "Enter" || e.key === " "
                                ? toggleIsRefAccordionOpen()
                                : null
                        }
                    >
                        <Stack style={{ width: "100%" }}>
                            <Stack
                                horizontal
                                horizontalAlign="start"
                                verticalAlign="center"
                            >
                                <Text
                                    onClick={toggleIsRefAccordionOpen}
                                    aria-label={chevronIsExpanded ? "Referenzen einklappen" : "Referenzen ausklappen"}
                                    aria-expanded={chevronIsExpanded}
                                    tabIndex={0}
                                    role="button"
                                >
                                    <span className={styles.accordionTitle}>
                                        {parsedAnswer.citations.length > 1
                                            ? parsedAnswer.citations.length +
                                              " Quellen"
                                            : "1 Quelle"}
                                    </span>
                                </Text>
                                <>
                                {!chevronIsExpanded && (
                                    <button
                                        onClick={handleChevronClick}
                                        aria-label="Referenzen ausklappen"
                                        style={{ display: 'flex', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                    >
                                        {HiChevronDown({ color: "#000000" })}
                                    </button>
                                )}
                                {chevronIsExpanded && (
                                    <button
                                        onClick={handleChevronClick}
                                        aria-label="Referenzen einklappen"
                                        style={{ display: 'flex', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                    >
                                        {HiChevronUp({ color: "#000000" })}
                                    </button>
                                )}
                                </>
                            </Stack>
                        </Stack>
                    </Stack.Item>
                )}
                {showActions && (
                    <Stack horizontal verticalAlign="center" style={{ marginLeft: "auto" }}>
                        <FeedbackButton messageId={messageId} traceId={traceId} />
                        <DefaultButton
                            className={`${styles.copyToClipboardBtn} ${styles.tooltip}`}
                            aria-label={texts?.texts.chat.buttons.copy ?? "Kopieren"}
                            onClick={() =>
                                navigator.clipboard.writeText(
                                    parsedAnswer.markdownFormatText
                                )
                            }
                        >
                            <img src={buttonCopyIcon} alt="" />
                            <span className={styles.tooltiptext}>
                                {texts?.texts.chat.buttons.copy ?? "Kopieren"}
                            </span>
                        </DefaultButton>
                    </Stack>
                )}
            </Stack>
            {chevronIsExpanded && (
                <div
                    style={{
                        marginTop: 8,
                        display: "flex",
                        flexFlow: "wrap column",
                        maxHeight: "150px",
                        gap: "4px",
                    }}
                >
                    {parsedAnswer.citations.map((citation, idx) => {
                        const { title, titleTruncated } =
                            createCitationFilename(citation, ++idx);
                        const extendedCitation = {
                            ...citation,
                            referenceTitle: title,
                        };
                        return (
                            <span
                                title={title}
                                tabIndex={0}
                                role="link"
                                key={idx}
                                onClick={() =>
                                    onCitationClicked(extendedCitation)
                                }
                                onKeyDown={(e) =>
                                    e.key === "Enter" || e.key === " "
                                        ? onCitationClicked(extendedCitation)
                                        : null
                                }
                                className={styles.citationContainer}
                                aria-label={title}
                            >
                                <div className={styles.citationIndex}>{idx}</div>
                                <div className={styles.citation}>{titleTruncated}</div>
                            </span>
                        );
                    })}
                </div>
            )}
        </Stack>
    );
};
